// Format a backend `event_date` string ("YYYY-MM-DD") into a human-
// readable date in the viewer's local timezone.
//
// Why this exists: `new Date("2026-05-05")` parses the string as UTC
// midnight. When the viewer is in any timezone west of UTC (all of the
// continental US is), `toLocaleDateString()` then renders the day
// before — e.g. "May 4" for an event that's actually on May 5. We
// dodge that by appending `T00:00:00` (no `Z`), which forces the
// constructor to interpret the date as midnight in *local* time.

export function formatEventDate(eventDate, options) {
  if (!eventDate || typeof eventDate !== 'string') return '';
  // Accept either "YYYY-MM-DD" or already-parseable strings (e.g. ISO
  // datetimes from older records). If it looks like the canonical
  // YYYY-MM-DD shape, append a local-time component; otherwise trust
  // whatever the parser does.
  const localish = /^\d{4}-\d{2}-\d{2}$/.test(eventDate)
    ? `${eventDate}T00:00:00`
    : eventDate;
  const d = new Date(localish);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', options);
}

// Return a YYYY-MM-DD string for the given input *in the viewer's local
// timezone*. Defaults to "now" if no input is provided.
//
// Why this exists: `new Date().toISOString().slice(0, 10)` is the
// classic shortcut — but `toISOString()` always returns the UTC date.
// For viewers in negative-offset timezones, around UTC midnight that
// shortcut yields *tomorrow's* date in their local time. This helper
// uses `getFullYear/getMonth/getDate` (which are local) instead.
//
// Use for: <input type="date"> values, `min` attrs, CSV filenames,
// slicing a backend ISO timestamp to its local-day part.
export function toLocalDateISO(input) {
  let d;
  if (!input) d = new Date();
  else if (input instanceof Date) d = input;
  else d = new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Build a relative label for upcoming events: "Today", "Tomorrow",
// "This Friday", "Next Tuesday". Returns null when the date is more
// than 13 days out (callers should fall back to the absolute date) or
// in the past.
export function relativeEventLabel(eventDate) {
  if (!eventDate || typeof eventDate !== 'string') return null;
  const localish = /^\d{4}-\d{2}-\d{2}$/.test(eventDate)
    ? `${eventDate}T00:00:00`
    : eventDate;
  const ev = new Date(localish);
  if (Number.isNaN(ev.getTime())) return null;
  const now = new Date();
  // Compare just the calendar day, not the time, so "today" works
  // regardless of when the page is loaded.
  const evDay = new Date(ev.getFullYear(), ev.getMonth(), ev.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((evDay - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return null;
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  const weekday = ev.toLocaleDateString('en-US', { weekday: 'long' });
  if (diffDays >= 2 && diffDays <= 6) return `This ${weekday}`;
  if (diffDays >= 7 && diffDays <= 13) return `Next ${weekday}`;
  return null;
}
